import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect
} from "react";
import { AzureIndex } from "@/pages/Messaging/Bots/types";
import useApi from "@/hooks/useApi";
import { AzureIndexesContext } from "@/context/AzureIndexesContext";

export function AzureIndexesProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const [azureIndexes, setAzureIndexes] = useState<AzureIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.messaging.get(`AzureIndexes/List`);
      if (!resp.ok) {
        // Keep the last-known-good list on a transient failure (e.g. a deploy slot swap) —
        // wiping to [] here made a mid-deploy blip look like every index vanished (2026-07-08).
        setError(
          `Could not load indexes (${resp.status}). Check your subscription or try refreshing.`
        );
        return;
      }
      const indexResult = (await resp.json()) as AzureIndex[];
      setAzureIndexes(Array.isArray(indexResult) ? indexResult : []);
    } catch {
      setError(
        "Could not reach the server. Check your connection and try refreshing."
      );
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const add = useCallback(async (result: AzureIndex) => {
    // Functional update + id-dedup: EnsureDefault can return an already-existing index
    // (promoted-legacy or a race with another tab) rather than a newly created one — without
    // the dedup check that would append a duplicate entry until the next full refetch.
    setAzureIndexes((prev) =>
      prev.some((ix) => ix.id === result.id) ? prev : [...prev, result]
    );
  }, []);

  const update = useCallback(
    async (result: AzureIndex) => {
      const clone = [...azureIndexes];
      const index = clone.find((index) => index.id === result.id);
      clone[clone.indexOf(index!)] = result;
      setAzureIndexes(clone);
    },
    [azureIndexes]
  );

  const remove = useCallback(
    async (id: number) => {
      const clone = [...azureIndexes];
      const deletedIndex = azureIndexes.find((index) => index.id === id);
      clone.splice(azureIndexes.indexOf(deletedIndex!), 1);
      setAzureIndexes(clone);
    },
    [azureIndexes]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <AzureIndexesContext.Provider
      value={{ azureIndexes, loading, error, add, remove, update }}
    >
      {props.children}
    </AzureIndexesContext.Provider>
  );
}
